import React from "react";
import { Car, Navigation } from "lucide-react";
import { Card, Input, Toggle, Textarea, PillGroup } from "./FormComponents";

export const Step12 = ({ data, updateData, isLight, showValidation }) => {
  const handleChange = (field, value) => {
    updateData("parkingTransport", { ...data, [field]: value });
  };

  const handleNestedChange = (parent, field, value) => {
    updateData("parkingTransport", {
      ...data,
      [parent]: {
        ...data[parent],
        [field]: value
      }
    });
  };

  return (
    <div className="flex flex-col gap-8">
      <Card title="Property and nearby parking options for guests." icon={Car} subtitle="Car access and local transport." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <PillGroup 
            label="Parking type" required 
            options={["No Parking", "Private On-site", "Street Parking", "Paid Nearby"]} 
            value={data.parking.type} isLight={isLight}
            showValidation={showValidation}
            onChange={(val) => {
              const updates = { 
                type: val,
                private: val === "Private On-site" ? data.parking.private : { type: "", spotAssignmentType: "", spotNumber: "", otherSpotSelection: "", accessRequired: false, accessMethod: "", evCharging: false },
                street: val === "Street Parking" ? data.parking.street : { instructions: "" },
                paid: val === "Paid Nearby" ? data.parking.paid : { name: "", address: "", hours: "", link: "", price: "" }
              };
              updateData("parkingTransport", { ...data, parking: { ...data.parking, ...updates } });
            }}
          />

          {data.parking.type === "Private On-site" && (
            <div className={`p-6 rounded-xl border flex flex-col gap-6 ${isLight ? 'bg-neutral-50/50 border-neutral-200' : 'bg-white/5 border-white/5'}`}>
              <PillGroup 
                label="Private parking type" required 
                options={["Parking Underground Garage", "Private Garage", "Outdoor Private Parking", "Driveway", "Courtyard Parking"]} 
                value={data.parking.private?.type || ""} isLight={isLight}
                showValidation={showValidation}
                onChange={(val) => handleNestedChange("parking", "private", { ...data.parking.private, type: val })}
              />

              <div className="flex flex-col gap-6">
                <PillGroup 
                  label="Is a specific parking spot assigned?" required
                  options={["Parking spot number", "Guests may use any available parking space", "Other"]}
                  value={data.parking.private?.spotAssignmentType || ""} isLight={isLight}
                  showValidation={showValidation}
                  onChange={(val) => {
                    const updates = { 
                      spotAssignmentType: val,
                      spotNumber: val === "Parking spot number" ? data.parking.private?.spotNumber : "",
                      otherSpotSelection: val === "Other" ? data.parking.private?.otherSpotSelection : ""
                    };
                    handleNestedChange("parking", "private", { ...data.parking.private, ...updates });
                  }}
                />
                
                {data.parking.private?.spotAssignmentType === "Parking spot number" && (
                  <Input 
                    label="Parking spot number" required placeholder="#12" 
                    value={data.parking.private?.spotNumber || ""} isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleNestedChange("parking", "private", { ...data.parking.private, spotNumber: e.target.value })}
                  />
                )}

                {data.parking.private?.spotAssignmentType === "Other" && (
                  <Input 
                    label="Specify other" required placeholder="Please specify..." 
                    value={data.parking.private?.otherSpotSelection || ""} isLight={isLight}
                    showValidation={showValidation}
                    onChange={(e) => handleNestedChange("parking", "private", { ...data.parking.private, otherSpotSelection: e.target.value })}
                  />
                )}
              </div>

              <div className={`pt-4 border-t flex flex-col gap-6 ${isLight ? 'border-neutral-200' : 'border-white/5'}`}>
                <Toggle 
                  label="Is access required to enter the parking area?" 
                  value={data.parking.private?.accessRequired || false} isLight={isLight}
                  onChange={(val) => {
                    const updates = { accessRequired: val };
                    if (!val) updates.accessMethod = "";
                    handleNestedChange("parking", "private", { ...data.parking.private, ...updates });
                  }}
                >
                  <PillGroup 
                    label="Parking access method" required
                    options={["Remote", "Key", "Key card / fob", "Access code / keypad"]}
                    value={data.parking.private?.accessMethod || ""} isLight={isLight}
                    showValidation={showValidation}
                    onChange={(val) => handleNestedChange("parking", "private", { ...data.parking.private, accessMethod: val })}
                  />
                </Toggle>

                <Toggle 
                  label="Is electric vehicle charging available?" 
                  value={data.parking.private?.evCharging || false} isLight={isLight}
                  onChange={(val) => handleNestedChange("parking", "private", { ...data.parking.private, evCharging: val })}
                />
              </div>
            </div>
          )}

          {data.parking.type === "Street Parking" && (
            <div className={`p-6 rounded-xl border ${isLight ? 'bg-neutral-50/50 border-neutral-200' : 'bg-white/5 border-white/5'}`}>
              <Textarea 
                label="Instructions" required
                placeholder="Free street parking is available on the surrounding blocks. Avoid yellow lines." 
                value={data.parking.street?.instructions || ""} isLight={isLight}
                showValidation={showValidation}
                onChange={(e) => handleNestedChange("parking", "street", { instructions: e.target.value })}
              />
            </div>
          )}

          {data.parking.type === "Paid Nearby" && (
            <div className={`p-6 rounded-xl border flex flex-col gap-6 ${isLight ? 'bg-neutral-50/50 border-neutral-200' : 'bg-white/5 border-white/5'}`}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Input 
                  label="Parking name" value={data.parking.paid?.name || ""} isLight={isLight}
                  onChange={(e) => handleNestedChange("parking", "paid", { ...data.parking.paid, name: e.target.value })}
                />
                <Input 
                  label="Address" value={data.parking.paid?.address || ""} isLight={isLight}
                  onChange={(e) => handleNestedChange("parking", "paid", { ...data.parking.paid, address: e.target.value })}
                />
                <Input 
                  label="Opening hours" value={data.parking.paid?.hours || ""} isLight={isLight}
                  onChange={(e) => handleNestedChange("parking", "paid", { ...data.parking.paid, hours: e.target.value })}
                />
                <Input 
                  label="Link for more options" type="url" 
                  value={data.parking.paid?.link || ""} isLight={isLight}
                  onChange={(e) => handleNestedChange("parking", "paid", { ...data.parking.paid, link: e.target.value })}
                />
                <div className="md:col-span-2">
                  <Textarea 
                    label="Price info" placeholder="Up to 2h: €2 / Up to 4h: €5 / After 4h: €10" 
                    value={data.parking.paid?.price || ""} isLight={isLight}
                    onChange={(e) => handleNestedChange("parking", "paid", { ...data.parking.paid, price: e.target.value })}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>

      <Card title="Transfer Services" icon={Navigation} subtitle="Guest transfer options available upon request." isLight={isLight}>
        <div className="flex flex-col gap-8">
          <Toggle 
            label="Is transfer service available upon request?" 
            value={data.transfer.available} isLight={isLight}
            onChange={(val) => handleNestedChange("transfer", "available", val)}
          />
        </div>
      </Card>
    </div>
  );
};
